import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import Currency from 'components/currency';
import { theme } from 'styles/configure-theme';
import { useFlag } from 'utils/launchdarkly';
import priceForCartEntry from 'utils/menu/price-for-cart-entry';

import { receiptFocusableLabeledProps } from '../receipt-a11y';

import { Item, ItemHeader, ItemName, ItemPrice } from './cart-entry.styled';
import resolveEntryChild, { collectEntryChildDescriptionParts } from './resolve-entry-child';

interface ICartEntry {
  entry: IBackendCartEntries;
  currencyFormatter: Function;
  index?: number;
}

const CartEntry = ({ entry, currencyFormatter }: ICartEntry) => {
  const quantity = entry.quantity || 1;
  const amount = priceForCartEntry(entry);
  const enablePremiumComboSlotsVariation = useFlag(
    'enable-premium-combo-slots'
  );
  const enableShowAllPrices = enablePremiumComboSlotsVariation !== 'none';
  const { formatMessage } = useIntl();
  const isDiscountOfferLine = entry.lineId === 'discount-offer';

  const primaryName = entry.isDonation
    ? formatMessage({ id: 'donation', description: '' })
    : entry.name ?? '';

  const lineAccessibilityLabel = useMemo(() => {
    if (isDiscountOfferLine) {
      return '';
    }
    const qtyPrefix = quantity > 1 ? `${quantity}x ` : '';
    const headline = `${qtyPrefix}${primaryName}, ${currencyFormatter(amount)}`;
    const modifierParts = collectEntryChildDescriptionParts(
      entry,
      currencyFormatter,
      enableShowAllPrices
    );
    return modifierParts.length ? `${headline}. ${modifierParts.join('. ')}` : headline;
  }, [
    amount,
    currencyFormatter,
    enableShowAllPrices,
    entry,
    isDiscountOfferLine,
    primaryName,
    quantity,
  ]);

  if (isDiscountOfferLine) {
    return null;
  }

  return (
    <Item {...receiptFocusableLabeledProps(lineAccessibilityLabel)}>
      <ItemHeader>
        <ItemName importantForAccessibility="no-hide-descendants">
          {quantity > 1 && `${quantity}x `}
          {entry.isDonation ? formatMessage({ id: 'donation', description: '' }) : entry.name}
        </ItemName>
        <ItemPrice>
          <Currency
            amount={amount}
            textProps={{
              ...(amount === 0 && { color: theme.token('icon-default') }),
              variant: 'headerFour',
              margin: 0,
              textAlign: 'right',
              importantForAccessibility: 'no-hide-descendants',
            }}
          />
        </ItemPrice>
      </ItemHeader>

      {resolveEntryChild(entry, currencyFormatter, enableShowAllPrices)}
    </Item>
  );
};

export default CartEntry;
